import { useState, useEffect } from 'react'
import { Row, Col, Card, Button, Form, Alert, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { useAuth } from '../hooks/useAuth'
import { api } from '../services/api'

interface ProfileData {
  name: string
  email: string
  currentPassword: string
  newPassword: string
  confirmPassword: string
}

export const ProfilePage = () => {
  const { user, updateUser } = useAuth()
  const [profileData, setProfileData] = useState<ProfileData>({
    name: user?.name || '',
    email: user?.email || '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  })
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')

  // Update form data when user changes
  useEffect(() => {
    if (user) {
      setProfileData(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }))
    }
  }, [user])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setProfileData(prev => ({
      ...prev,
      [name]: value
    }))
  }

  const handleProfileUpdate = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    try {
      // Update profile information
      const response = await api.patch('/users/profile', {
        name: profileData.name
      })

      // Update user in auth context
      if (response.data.data && updateUser) {
        updateUser(response.data.data)
      }

      toast.success('Profile updated successfully!', {
        icon: '✅',
        autoClose: 3000
      })
      setMessage('Profile updated successfully!')
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to update profile'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    setMessage('')

    if (profileData.newPassword !== profileData.confirmPassword) {
      setError('New passwords do not match')
      setLoading(false)
      return
    }

    if (profileData.newPassword.length < 6) {
      setError('Password must be at least 6 characters long')
      setLoading(false)
      return
    }

    try {
      await api.patch('/users/password', {
        currentPassword: profileData.currentPassword,
        newPassword: profileData.newPassword
      })

      toast.success('Password updated successfully!', {
        icon: '✅',
        autoClose: 3000
      })
      setMessage('Password updated successfully!')
      setProfileData(prev => ({
        ...prev,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      }))
    } catch (err: any) {
      const errorMessage = err.response?.data?.error?.message || 'Failed to update password'
      setError(errorMessage)
      toast.error(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="profile-container">
      {/* Header */}
      <div className="mb-4">
        <h1 className="mb-1">Profile Settings</h1>
        <p className="text-muted mb-0">Manage your account information and security</p>
      </div>

      <Row>
        <Col lg={8}>
          {/* Profile Information */}
          <Card className="mb-4 border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="fas fa-user me-2 text-primary"></i>
                Profile Information
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handleProfileUpdate}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={profileData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Email Address</Form.Label>
                      <Form.Control
                        type="email"
                        value={profileData.email}
                        disabled
                        className="bg-light"
                      />
                      <Form.Text className="text-muted">
                        Email cannot be changed. Contact administrator if needed.
                      </Form.Text>
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Role</Form.Label>
                      <Form.Control
                        type="text"
                        value={user?.role || 'User'}
                        disabled
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Organization</Form.Label>
                      <Form.Control
                        type="text"
                        value={user?.org?.name || 'Demo Organization'}
                        disabled
                        className="bg-light"
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button 
                    type="submit" 
                    variant="primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Update Profile
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>

          {/* Password Change */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="fas fa-lock me-2 text-warning"></i>
                Change Password
              </h5>
            </Card.Header>
            <Card.Body>
              <Form onSubmit={handlePasswordChange}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Current Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="currentPassword"
                        value={profileData.currentPassword}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="newPassword"
                        value={profileData.newPassword}
                        onChange={handleInputChange}
                        required
                        minLength={6}
                      />
                      <Form.Text className="text-muted">
                        Password must be at least 6 characters long
                      </Form.Text>
                    </Form.Group>
                  </Col>
                  
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        name="confirmPassword"
                        value={profileData.confirmPassword}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <div className="d-flex justify-content-end">
                  <Button 
                    type="submit" 
                    variant="warning"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <Spinner size="sm" className="me-2" />
                        Updating...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-key me-2"></i>
                        Change Password
                      </>
                    )}
                  </Button>
                </div>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col lg={4}>
          {/* Account Summary */}
          <Card className="border-0 shadow-sm mb-4">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="fas fa-info-circle me-2 text-info"></i>
                Account Summary
              </h5>
            </Card.Header>
            <Card.Body>
              <div className="text-center mb-3">
                <div className="bg-primary rounded-circle p-3 d-inline-block mb-3">
                  <i className="fas fa-user text-white" style={{ fontSize: '2rem' }}></i>
                </div>
                <h5>{user?.name || 'User'}</h5>
                <p className="text-muted">{user?.email || 'user@example.com'}</p>
              </div>
              
              <div className="border-top pt-3">
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Role:</span>
                  <span className="fw-medium">{user?.role || 'User'}</span>
                </div>
                <div className="d-flex justify-content-between mb-2">
                  <span className="text-muted">Organization:</span>
                  <span className="fw-medium">{user?.org?.name || 'Demo'}</span>
                </div>
                <div className="d-flex justify-content-between">
                  <span className="text-muted">Member Since:</span>
                  <span className="fw-medium">2024</span>
                </div>
              </div>
            </Card.Body>
          </Card>

          {/* Security Tips */}
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <h5 className="mb-0">
                <i className="fas fa-shield-alt me-2 text-success"></i>
                Security Tips
              </h5>
            </Card.Header>
            <Card.Body>
              <ul className="list-unstyled mb-0">
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  Use a strong, unique password
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  Don't share your login credentials
                </li>
                <li className="mb-2">
                  <i className="fas fa-check text-success me-2"></i>
                  Log out when using shared computers
                </li>
                <li className="mb-0">
                  <i className="fas fa-check text-success me-2"></i>
                  Report suspicious activity immediately
                </li>
              </ul>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Messages */}
      {message && (
        <Alert variant="success" className="mt-3">
          <i className="fas fa-check-circle me-2"></i>
          {message}
        </Alert>
      )}

      {error && (
        <Alert variant="danger" className="mt-3">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error}
        </Alert>
      )}
    </div>
  )
}

